import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cx } from "../react-helpers/css";

const ELLIPSIS = "…";

/**
 * Build the list of page numbers to display, inserting `null` markers where
 * pages are collapsed into an ellipsis. Always keeps the first/last page and a
 * window around the current page.
 */
const buildPageItems = (
  currentPage: number,
  pageCount: number,
): (number | null)[] => {
  const siblings = 1;
  const pages = new Set<number>([1, pageCount]);
  for (let p = currentPage - siblings; p <= currentPage + siblings; p++) {
    if (p >= 1 && p <= pageCount) pages.add(p);
  }

  const sorted = [...pages].sort((a, b) => a - b);
  const items: (number | null)[] = [];
  let previous = 0;
  for (const page of sorted) {
    if (previous && page - previous > 1) items.push(null);
    items.push(page);
    previous = page;
  }
  return items;
};

const Pagination = ({
  currentPage,
  pageCount,
  onPageChange,
}: {
  currentPage: number;
  pageCount: number;
  onPageChange: (page: number) => void;
}) => {
  const { t } = useTranslation();

  const items = useMemo(
    () => buildPageItems(currentPage, pageCount),
    [currentPage, pageCount],
  );

  if (pageCount <= 1) return null;

  return (
    <nav
      className="pagination"
      aria-label={t("common:pagination.PAGE_INFO", {
        current: currentPage,
        total: pageCount,
      })}
    >
      <div className="pagination_controls">
        <button
          type="button"
          className="btn--soft --btn--s pagination_nav"
          disabled={currentPage <= 1}
          onClick={() => onPageChange(currentPage - 1)}
        >
          {t("common:pagination.PREVIOUS")}
        </button>

        {items.map((page, index) =>
          page === null ? (
            <span
              key={`ellipsis-${index}`}
              className="pagination_ellipsis"
              aria-hidden
            >
              {ELLIPSIS}
            </span>
          ) : (
            <button
              key={page}
              type="button"
              className={cx([
                "--btn--s",
                "pagination_page",
                page === currentPage ? "btn" : "btn--white",
              ])}
              aria-current={page === currentPage ? "page" : undefined}
              aria-label={t("common:pagination.PAGE_LABEL", { page })}
              onClick={() => onPageChange(page)}
            >
              {page}
            </button>
          ),
        )}

        <button
          type="button"
          className="btn--soft --btn--s pagination_nav"
          disabled={currentPage >= pageCount}
          onClick={() => onPageChange(currentPage + 1)}
        >
          {t("common:pagination.NEXT")}
        </button>
      </div>

      <p className="pagination_info">
        {t("common:pagination.PAGE_INFO", {
          current: currentPage,
          total: pageCount,
        })}
      </p>
    </nav>
  );
};

export default Pagination;
